import type { BackendClient } from '@playbooks/core/src/services/BackendClient'
import type {
  AiTeammateRealtimeModel,
  AiTeammateTranscriptionLanguage,
  Section,
} from '@playbooks/core/src/interfaces/ActionCard'
import { StreamSpeechDetection } from './audio/streamSpeechDetection'
import type { TeammateAudioSourceController } from './audio/teammateAudioSourceController'
import type { SpeakerLabel } from './audio/utterance'
import type { UtteranceFeed } from './audio/utteranceFeed'
import {
  TeammateRealtimeController,
  type TeammateRealtimeErrorCode,
  type TeammateRealtimeEvent,
  type TeammateRealtimeStatus,
} from './realtime/teammateRealtimeController'
import { TeammateTranscript } from './teammateTranscript'

/** Widget settings that decide which audio and which OpenAI connections a listening session uses. */
export interface TeammateListeningSettings {
  /**
   * Record the transcript. With a capture app that captures both the microphone and the system audio,
   * the transcript lines get speaker labels.
   */
  recordTranscript: boolean
  fillAutomatically: boolean
  transcriptionLanguage?: AiTeammateTranscriptionLanguage
  model?: AiTeammateRealtimeModel
}

/** The saved transcript, which goes into the Action Card, and the interim text, which is only displayed. */
export interface TeammateTranscriptSnapshot {
  transcript: string
  interim: string
}

/** Dependencies and callbacks of a {@link TeammateListeningSession}. */
interface TeammateListeningSessionParams {
  backendClient: BackendClient
  /** Shared with later sessions: it keeps the connection to the capture app between sessions. */
  audioSource: TeammateAudioSourceController
  settings: TeammateListeningSettings
  actionCardSections: Section[]
  /** The transcript stored in the widget so far. New text is appended to it. */
  savedTranscript: string
  /** Translate a speaker label into the widget's display language. */
  speakerName: (speaker: SpeakerLabel) => string
  getErrorMessage: (code: TeammateRealtimeErrorCode) => string
  /** Every OpenAI event of every connection, for filling the playbook and for errors. */
  onEvent: (event: TeammateRealtimeEvent) => void
  /**
   * The combined status of all connections. `listening` when every connection is ready.
   * `stopped` when one connection stopped by itself, for example after failed reconnects:
   * the widget then stops the complete session.
   */
  onStatusChange: (status: TeammateRealtimeStatus) => void
  onTranscriptChange: (snapshot: TeammateTranscriptSnapshot) => void
  /** A fatal error of one connection. Its `stopped` status follows. */
  onError: (message: string) => void
}

/** One OpenAI connection of the session, and the audio it sends. */
interface Connection {
  controller: TeammateRealtimeController
  stream: MediaStream
  /** Where the utterances of the audio end. Without it, OpenAI detects them (playbook filling). */
  utterances?: UtteranceFeed
}

/**
 * Runs one listening session of the AI Teammate widget: from the start of listening until it stops.
 * It starts the audio, opens the OpenAI connections, and builds the transcript from their events.
 *
 * Connections:
 * - Ordinary transcription: one connection with the mixed audio of the capture app, or with the browser microphone.
 *   It transcribes, fills the playbook, or both.
 * - Speaker attribution: one transcription connection for the microphone ("Me"), and one for the system audio
 *   ("Remote"). Automatic filling adds a third connection with the mixed audio.
 * All connections share the audio, but reconnect independently.
 */
export class TeammateListeningSession {
  private connections: Connection[] = []
  private readonly statuses = new Map<TeammateRealtimeController, TeammateRealtimeStatus>()
  /** Speech detection for ordinary transcription. Null in other cases. */
  private speechDetection: StreamSpeechDetection | null = null
  private transcript: TeammateTranscript | null = null
  /** True while the connections stop, so their stopped statuses are not reported as unexpected. */
  private stopping = false
  /** True after a stop or dispose. A start that is still running then releases its audio and connects nothing. */
  private closed = false

  constructor(private readonly params: TeammateListeningSessionParams) {}

  /**
   * Start the audio and connect to OpenAI.
   * With speaker attribution, use one transcription connection for the microphone and one for the system audio,
   * and add the mixed-audio connection only for automatic filling.
   * Otherwise, use one mixed-audio connection, which keeps the microphone fallback and older capture apps available.
   */
  async start() {
    const { settings } = this.params
    // Speaker labels are useful only in a transcript.
    const audio = await this.params.audioSource.startPreferredSource(settings.recordTranscript)
    // The widget can stop or unmount while the audio starts, for example during the microphone permission prompt.
    if (this.closed) return this.stopAudio()
    const speakerAttribution = audio.speakerChannels.length > 0
    this.transcript = new TeammateTranscript(this.params.savedTranscript, speakerAttribution)

    if (settings.fillAutomatically || !speakerAttribution) {
      // Detect utterances for ordinary transcription. With filling, OpenAI detects them itself.
      // With speaker attribution, the speaker connections create the transcript, not the mixed audio.
      this.speechDetection = settings.fillAutomatically ? null : new StreamSpeechDetection(audio.stream)
      this.connections.push({
        controller: this.createController({
          recordTranscript: settings.recordTranscript && !speakerAttribution,
          fillAutomatically: settings.fillAutomatically,
          model: settings.model,
        }),
        stream: audio.stream,
        utterances: this.speechDetection?.utterances,
      })
    }
    for (const { stream, utterances } of audio.speakerChannels) {
      this.connections.push({
        controller: this.createController({ recordTranscript: true, fillAutomatically: false }),
        stream,
        utterances,
      })
    }

    await Promise.all(this.connections.map(({ controller, stream, utterances }) => controller.start(stream, utterances)))
  }

  /**
   * Finish all connections before stopping microphone or app capture.
   * Capture must continue until the final utterances and queued audio reach the media tracks.
   * Otherwise the last words can be lost.
   * Then save all remaining text, including words without a final result.
   */
  async stop() {
    this.closed = true
    this.stopping = true
    try {
      // Keep capture running until all connections have received their final transcripts.
      await Promise.all(this.connections.map(({ controller }) => controller.stop()))
      this.transcript?.finish(this.params.speakerName)
      this.reportTranscript()
      this.stopAudio()
    } finally {
      this.stopping = false
    }
  }

  /** Close all connections and stop the audio without waiting for final transcripts, for example on unmount. */
  async dispose() {
    this.closed = true
    const connections = this.connections
    this.connections = []
    this.statuses.clear()
    await Promise.all(connections.map(({ controller }) => controller.dispose()))
    this.stopAudio()
  }

  /** Return whether any connection is meant to keep listening. */
  isActive() {
    return this.connections.some(({ controller }) => controller.isActive())
  }

  /** Use the latest Action Card sections for the next OpenAI sessions, for example after a reconnect. */
  setActionCardSections(sections: Section[]) {
    this.connections.forEach(({ controller }) => controller.setActionCardSections(sections))
  }

  /**
   * Create one OpenAI controller and register its status before any connection starts.
   * All status entries must exist before the first connection reports listening.
   * Otherwise the UI can show listening too early.
   */
  private createController(options: Pick<TeammateListeningSettings, 'recordTranscript' | 'fillAutomatically' | 'model'>) {
    const controller = new TeammateRealtimeController({
      ...options,
      backendClient: this.params.backendClient,
      transcriptionLanguage: this.params.settings.transcriptionLanguage,
      getErrorMessage: this.params.getErrorMessage,
      onEvent: event => this.handleEvent(event),
      onStatusChange: status => this.handleStatusChange(controller, status),
      onError: this.params.onError,
    })
    controller.setActionCardSections(this.params.actionCardSections)
    this.statuses.set(controller, 'connecting')
    return controller
  }

  /** Pass an event to the widget, and add its text to the transcript. */
  private handleEvent(event: TeammateRealtimeEvent) {
    this.params.onEvent(event)
    // Final transcripts can arrive in any order. The transcript restores the conversation order.
    if (this.params.settings.recordTranscript && this.transcript) {
      this.transcript.accept(event)
      this.reportTranscript()
    }
  }

  /**
   * Combine all connection statuses into one session status.
   * Report listening only when every connection is ready. A stopped connection ends the complete listening session.
   */
  private handleStatusChange(controller: TeammateRealtimeController, status: TeammateRealtimeStatus) {
    if (!this.statuses.has(controller)) return
    this.statuses.set(controller, status)
    const statuses = [...this.statuses.values()]
    if (status === 'stopped') {
      if (!this.stopping) this.params.onStatusChange('stopped')
    } else if (statuses.some(value => value === 'connecting')) {
      this.params.onStatusChange('connecting')
    } else if (statuses.every(value => value === 'listening')) {
      this.params.onStatusChange('listening')
    }
  }

  private reportTranscript() {
    if (this.transcript) this.params.onTranscriptChange(this.transcript.snapshot(this.params.speakerName))
  }

  private stopAudio() {
    this.speechDetection?.stop()
    this.speechDetection = null
    this.params.audioSource.stop()
  }
}
